import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Spinner, Alert, Pagination, Form } from 'react-bootstrap';
import { fetchStatisticsRequest } from '../store/actions/statisticsActions';
import { ORAKLE_WEATHER_LOGO_URL } from '../constants/colors';
import {
    FiUsers,
    FiFileText,
    FiAlertTriangle,
    FiTrendingUp,
    FiCalendar,
    FiActivity,
} from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './Welcome.css';
import {
    getPoliciesActivities,
    getPoliciesTrips,
    getClaims,
    getForecastsNotTriggeredTrips,
    getPartners,
    getPoliciesTripsB2C,
    getClaimsTripsB2C,
} from '../services/api';

const Welcome = () => {
    const dispatch = useDispatch();
    const { statistics } = useSelector((state) => state.data);
    const { accessToken } = useSelector((state) => state.adminAuth);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [metaCounts, setMetaCounts] = useState({
        policiesActivities: null,
        policiesTrips: null,
        claims: null,
        forecastNotTriggered: null,
        partners: null,
        policiesTripsB2C: null,
        claimsTripsB2C: null,
    });
    const [metaError, setMetaError] = useState(null);
    const [metaLoading, setMetaLoading] = useState(false);

    const { statsObject, statsMeta, statsArray } = useMemo(() => {
        const raw = statistics.data;
        const meta = raw?.meta || null;
        let list = null;

        if (Array.isArray(raw?.data)) {
            list = raw.data;
        } else if (Array.isArray(raw?.items)) {
            list = raw.items;
        } else if (Array.isArray(raw?.results)) {
            list = raw.results;
        } else if (Array.isArray(raw)) {
            list = raw;
        } else if (raw?.data && typeof raw.data === 'object') {
            list = [raw.data];
        }

        const object = list && list.length > 0
            ? list[0]
            : (raw && !Array.isArray(raw) ? raw : {});

        return {
            statsObject: object || {},
            statsMeta: meta,
            statsArray: list,
        };
    }, [statistics.data]);

    useEffect(() => {
        if (statsMeta?.page && statsMeta.page !== currentPage) {
            setCurrentPage(statsMeta.page);
        }
    }, [statsMeta?.page]); // eslint-disable-line react-hooks/exhaustive-deps

    const statsData = statsObject || {};

    const derivedMeta = useMemo(() => {
        if (statsMeta && typeof statsMeta === 'object') {
            return statsMeta;
        }
        if (statsArray) {
            return {
                total: statsArray.length,
                skip: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                page: currentPage,
                total_pages: Math.max(1, Math.ceil(statsArray.length / itemsPerPage)),
            };
        }
        return null;
    }, [statsMeta, statsArray, currentPage, itemsPerPage]);

    const totalPages = derivedMeta?.total_pages ?? 1;
    const currentPageFromMeta = derivedMeta?.page ?? currentPage;
    const totalItems = derivedMeta?.total ?? (statsArray ? statsArray.length : 0);

    const paginationPages = useMemo(() => {
        if (totalPages <= 1) return [];
        const range = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPageFromMeta - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let page = startPage; page <= endPage; page += 1) {
            range.push(page);
        }

        return range;
    }, [currentPageFromMeta, totalPages]);

    const handleLimitChange = (event) => {
        const value = Number(event.target.value);
        if (!Number.isNaN(value)) {
            setItemsPerPage(value);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        if (page !== currentPage) {
            setCurrentPage(page);
        }
    };

    const showPagination = totalPages > 1;
    const hasStatistics = statsData && Object.keys(statsData).length > 0;
    const noStatisticsAvailable = !statistics.loading && !statistics.error && !hasStatistics;

    const policiesMeta = metaCounts.policiesActivities;
    const policiesValue = policiesMeta?.total_pages ?? statsData.policies?.total ?? 0;
    const policiesSubtitle = policiesMeta
        ? `Total : ${policiesMeta.total ?? 0} éléments`
        : `${statsData.policies?.active || 0} actives, ${statsData.policies?.expired || 0} expirées`;

    const tripPoliciesMeta = metaCounts.policiesTrips;
    const tripPoliciesValue = tripPoliciesMeta?.total_pages ?? statsData.trip_policies?.total ?? 0;
    const tripPoliciesSubtitle = tripPoliciesMeta
        ? `Total : ${tripPoliciesMeta.total ?? 0} éléments`
        : `${statsData.trip_policies?.active_today || 0} actives aujourd'hui`;

    const activityPoliciesValue = policiesMeta?.total_pages ?? statsData.activity_policies?.total ?? policiesValue;
    const activityPoliciesSubtitle = policiesMeta
        ? `Total : ${policiesMeta.total ?? 0} éléments`
        : `${statsData.activity_policies?.active_today || 0} actives aujourd'hui`;

    const b2cPoliciesMeta = metaCounts.policiesTripsB2C;
    const b2cPoliciesValue = b2cPoliciesMeta?.total ?? 0;
    const b2cPoliciesSubtitle = b2cPoliciesMeta
        ? `Total : ${b2cPoliciesMeta.total ?? 0} éléments`
        : 'Aucune donnée disponible';

    const b2cClaimsMeta = metaCounts.claimsTripsB2C;
    const b2cClaimsValue = b2cClaimsMeta?.total ?? 0;
    const b2cClaimsSubtitle = b2cClaimsMeta
        ? `Total : ${b2cClaimsMeta.total ?? 0} éléments`
        : 'Aucune donnée disponible';

    const claimsMeta = metaCounts.claims;
    const claimsValue = claimsMeta?.total_pages ?? statsData.claims?.total ?? 0;
    const claimsSubtitle = claimsMeta
        ? `Total : ${claimsMeta.total ?? 0} éléments`
        : (statsData.claims?.by_status?.Validated ? `${statsData.claims.by_status.Validated} validées` : 'Aucune');

    const forecastMeta = metaCounts.forecastNotTriggered;
    const forecastValue = forecastMeta?.total_pages ?? statsData.forecast_not_triggered?.total ?? 0;
    const forecastSubtitle = forecastMeta
        ? `Total : ${forecastMeta.total ?? 0} éléments`
        : `${statsData.forecast_not_triggered?.trips || 0} trips, ${statsData.forecast_not_triggered?.activities || 0} activités`;

    // Get admin name from statistics API response
    const getUserFullName = () => {
        if (statsData?.admin?.first_name) {
            const firstName = statsData.admin.first_name;
            const lastName = statsData.admin.last_name || '';
            return lastName ? `${firstName} ${lastName}` : firstName;
        }
        // Fallback: Try to get from localStorage if stored during login
        const email = localStorage.getItem('admin_email');
        if (email) {
            // Extract first part of email (before @)
            const name = email.split('@')[0];
            // Capitalize first letter
            return name.charAt(0).toUpperCase() + name.slice(1);
        }
        return 'Administrateur';
    };

    const fullName = getUserFullName();

    useEffect(() => {
        if (accessToken) {
            const skip = (currentPage - 1) * itemsPerPage;
            dispatch(fetchStatisticsRequest({ skip, limit: itemsPerPage }));
        }
    }, [dispatch, accessToken, currentPage, itemsPerPage]);

    useEffect(() => {
        if (!accessToken) return;

        let isMounted = true;
        const loadMetaCounts = async () => {
            try {
                setMetaLoading(true);
                setMetaError(null);

                const [policiesActRes, policiesTripRes, claimsRes, forecastRes, partnersRes, policiesB2CRes, claimsB2CRes] = await Promise.all([
                    getPoliciesActivities({ skip: 0, limit: 1 }),
                    getPoliciesTrips({ skip: 0, limit: 1 }),
                    getClaims({ skip: 0, limit: 1 }),
                    getForecastsNotTriggeredTrips({ skip: 0, limit: 1 }),
                    getPartners({ skip: 0, limit: 1 }),
                    getPoliciesTripsB2C({ skip: 0, limit: 1 }),
                    getClaimsTripsB2C({ skip: 0, limit: 1 }),
                ]);

                if (!isMounted) return;

                setMetaCounts({
                    policiesActivities: policiesActRes?.meta || null,
                    policiesTrips: policiesTripRes?.meta || null,
                    claims: claimsRes?.meta || null,
                    forecastNotTriggered: forecastRes?.meta || null,
                    partners: partnersRes?.meta || null,
                    policiesTripsB2C: policiesB2CRes?.meta || null,
                    claimsTripsB2C: claimsB2CRes?.meta || null,
                });
            } catch (error) {
                if (!isMounted) return;
                if (error.name !== 'AbortError') {
                    setMetaError(error.message || 'Erreur lors du chargement des statistiques détaillées.');
                }
            } finally {
                if (isMounted) {
                    setMetaLoading(false);
                }
            }
        };

        loadMetaCounts();
        return () => {
            isMounted = false;
        };
    }, [accessToken]);

    const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
        <div className="stat-card">
            <div className={`stat-icon stat-icon-${color}`}>
                <Icon size={24} />
            </div>
            <div className="stat-info">
                <h3>{title}</h3>
                <div className="stat-value">{value || '-'}</div>
                {subtitle && <div className="stat-subtitle">{subtitle}</div>}
            </div>
        </div>
    );

    const getCurrentGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Bonjour';
        if (hour < 18) return 'Bon après-midi';
        return 'Bonsoir';
    };

    return (
        <div className="welcome-page">
            {/* Welcome Header */}
            <Card className="welcome-header mb-4">
                <Card.Body className="welcome-header-body">
                    <div className="welcome-header-content">
                        <div>
                            <h1 className="welcome-title">
                                <span className="welcome-greeting">{getCurrentGreeting()}</span>, <span className="welcome-name">{fullName}</span> !
                            </h1>
                            <p className="welcome-subtitle">
                                Bienvenue sur l'admin plateforme Orakle Weather
                            </p>
                        </div>

                    </div>
                </Card.Body>
            </Card>


            {/* Statistics Section */}
            {statistics.loading && (
                <div className="text-center py-5" style={{ color: 'var(--text-primary)' }}>
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>Chargement des statistiques...</p>
                </div>
            )}

            {statistics.error && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Erreur de chargement</Alert.Heading>
                    <p>Impossible de charger les statistiques : {statistics.error}</p>
                </Alert>
            )}

            {metaError && (
                <Alert variant="warning" className="mb-4">
                    {metaError}
                </Alert>
            )}

            {noStatisticsAvailable && (
                <Alert variant="info" className="mb-4">
                    Aucune statistique disponible pour le moment.
                </Alert>
            )}

            {hasStatistics && (
                <div className="statistics-section mb-5">
                    <h3 className="section-title">
                        <FiTrendingUp className="me-2" style={{ color: 'var(--attijari-primary)' }} />
                        Aperçu des statistiques
                    </h3>

                    {metaLoading && (
                        <div className="text-muted small mb-3">
                            Mise à jour des compteurs via les endpoints paginés...
                        </div>
                    )}

                    {derivedMeta && (
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
                            <div>
                                <strong>Page {currentPageFromMeta} sur {totalPages}</strong>
                                {typeof totalItems === 'number' && totalItems >= 0 && (
                                    <span className="text-muted ms-2">Total : {totalItems}</span>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted">Éléments par page :</span>
                                <Form.Select
                                    size="sm"
                                    value={itemsPerPage}
                                    onChange={handleLimitChange}
                                    style={{ width: 'auto' }}
                                >
                                    {[10, 20, 50, 100].map((value) => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>
                    )}

                    {showPagination && (
                        <div className="d-flex justify-content-center mb-4">
                            <Pagination className="mb-0">
                                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPageFromMeta === 1} />
                                <Pagination.Prev onClick={() => handlePageChange(currentPageFromMeta - 1)} disabled={currentPageFromMeta === 1} />
                                {paginationPages.map((page) => (
                                    <Pagination.Item
                                        key={page}
                                        active={page === currentPageFromMeta}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next onClick={() => handlePageChange(currentPageFromMeta + 1)} disabled={currentPageFromMeta === totalPages} />
                                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPageFromMeta === totalPages} />
                            </Pagination>
                        </div>
                    )}

                    {/* GENERAL and TRIP Sections in same row */}
                    <div className="stat-groups-row mb-4">
                        {/* GENERAL Section */}
                        <div className="stat-group">
                            <h4 className="stat-group-title">GENERAL</h4>
                            <div className="stats-grid">
                                <StatCard
                                    icon={FiUsers}
                                    title="Clients"
                                    value={statsData.clients?.total || 0}
                                    subtitle={`${statsData.clients?.created_last_30_days || 0} créés les 30 derniers jours`}
                                    color="blue"
                                />
                                <StatCard
                                    icon={FiUsers}
                                    title="Partenaires"
                                    value={metaCounts.partners?.total ?? statsData.users?.total ?? 0}
                                    subtitle={metaCounts.partners
                                        ? `Total : ${metaCounts.partners.total ?? 0} partenaires`
                                        : `${statsData.users?.active || 0} actifs`}
                                    color="purple"
                                />
                            </div>
                        </div>

                        {/* TRIP Section */}
                        <div className="stat-group">
                            <h4 className="stat-group-title">TRIP</h4>
                            <div className="stats-grid">
                                <StatCard
                                    icon={FiActivity}
                                    title="Polices Trip"
                                    value={tripPoliciesValue}
                                    subtitle={tripPoliciesSubtitle}
                                    color="blue"
                                />
                                <StatCard
                                    icon={FiAlertTriangle}
                                    title="Sinistres Trip"
                                    value={statsData.trip_claims?.total || 0}
                                    subtitle={`${statsData.trip_claims?.automatic || 0} automatiques, ${statsData.trip_claims?.manual || 0} manuelles`}
                                    color="orange"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ACTIVITY and B2C Sections in same row */}
                    <div className="stat-groups-row mb-4">
                        {/* ACTIVITY Section */}
                        <div className="stat-group">
                            <h4 className="stat-group-title">ACTIVITY</h4>
                            <div className="stats-grid">
                                <StatCard
                                    icon={FiActivity}
                                    title="Polices Activity"
                                    value={activityPoliciesValue}
                                    subtitle={activityPoliciesSubtitle}
                                    color="green"
                                />
                                <StatCard
                                    icon={FiAlertTriangle}
                                    title="Sinistres Activity"
                                    value={statsData.activity_claims?.total || 0}
                                    subtitle={statsData.activity_claims?.by_status
                                        ? `${statsData.activity_claims.by_status.Validated || 0} validées`
                                        : 'Aucune'}
                                    color="orange"
                                />
                            </div>
                        </div>

                        {/* B2C Section */}
                        <div className="stat-group">
                            <h4 className="stat-group-title">B2C</h4>
                            <div className="stats-grid">
                                <StatCard
                                    icon={FiFileText}
                                    title="Polices B2C"
                                    value={b2cPoliciesValue}
                                    subtitle={b2cPoliciesSubtitle}
                                    color="blue"
                                />
                                <StatCard
                                    icon={FiAlertTriangle}
                                    title="Sinistres B2C"
                                    value={b2cClaimsValue}
                                    subtitle={b2cClaimsSubtitle}
                                    color="orange"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Welcome;
